import '../../platform.css';
import './searchPlayers.css';

import { useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../recoil/atoms/userConfigState';
import { playersState } from '../../recoil/atoms/playersState';
import { playerOverviewsState } from '../../recoil/atoms/playerOverviewsState';
import { useSetSelectedPlayer } from '../../recoil/hooks/useSetSelectedPlayer';
import { scoutingConfigState } from '../../recoil/atoms/scoutingConfigState';
import { useAuthContext } from '../../../common/contexts/useAuthContext';

import Fuse from 'fuse.js';

import { translate } from '../../../common/language/translations';
import { PlayerSimpleTable } from '../tables/playerSimpleTable/PlayerSimpleTable';
import { InputField } from '../controls/input/InputField';
import { debounce } from '../../utils/commonUtils';
import { searchPlayerOverviews } from '../../services/server/application/playerOverviews';
import { playerSimpleTablePageSize } from '../../views/scouting/Scouting';
import { PlayerDocument, PlayerId } from '../../types/player';
import { StringToAnyMap } from '../../types/common';
import { PlayerOverviewsQueryOptions, SearchPlayerOverviewsResponse } from '../../types/scouting';


interface AddPlayerAcademyViewProps {
  searchString: string;
  setSearchString: (value: string) => void;
  isAddPlayerWithoutData: boolean;
  setIsAddPlayerWithoutData: (value: boolean) => void;
  isFiltersExpanded: boolean;
}

export const AddPlayerAcademyView: React.FC<AddPlayerAcademyViewProps> = ({
  searchString,
  setSearchString,
  isAddPlayerWithoutData,
  setIsAddPlayerWithoutData,
  isFiltersExpanded,
}) => {

  const { currentUser } = useAuthContext();
  const userConfig = useRecoilValue(userConfigState);
  const scoutingConfig = useRecoilValue(scoutingConfigState);

  const players = useRecoilValue(playersState);
  const playerOverviews = useRecoilValue(playerOverviewsState);
  const { setAndTrackSelectedPlayer } = useSetSelectedPlayer();

  const [existingPlayersWithoutData, setExistingPlayersWithoutData] = useState<PlayerDocument[]>([]);
  const [existingPlayersWithoutDataToShow, setExistingPlayersWithoutDataToShow] = useState<PlayerDocument[]>([]);
  const [existingPlayersWithoutDataSearchString, setExistingPlayersWithoutDataSearchString] = useState<string>('');

  const [debouncedSearchString, setDebouncedSearchString] = useState<string>('');
  const clearDebounceRef = useRef<() => void>(() => null);

  const [activeSearchString, setActiveSearchString] = useState('');
  const [shouldShowSearchResult, setShouldShowSearchResult] = useState(false);
  const [isSearchWithoutResult, setIsSearchWithoutResult] = useState(false);

  const [tableData, setTableData] = useState<StringToAnyMap[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalHits, setTotalHits] = useState(0);

  const [showText, setShowText] = useState(false);


  const onPlayerClick = (player: StringToAnyMap) => {
    const playerId = Number(player.id);
    const isPlayerWithData = !isNaN(playerId);

    const playerOverview = playerOverviews[playerId] ?? (
      isPlayerWithData
        ? player
        : undefined
    );

    setAndTrackSelectedPlayer({
      id: player.id,
      name: player.name,
      playerOverview: playerOverview,
    });
  };


  const onChangeSearchField = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(event.target.value);
  };


  const onKeyDownSearchField = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (!isAddPlayerWithoutData) {
        handleSearchForPlayers(true, true);
      }
    }
  };


  const setEmptyTableData = () => {
    setTableData([]);
    setCurrentPage(0);
    setTotalHits(0);
  };


  const handleSearchForPlayers = async (isUserInitiated: boolean, isNewSearch: boolean) => {

    setIsLoading(true);

    if (isUserInitiated) {
      clearDebounceRef.current();
    }

    if (searchString !== '') {

      const nextPageToQuery = isNewSearch ? 1 : currentPage + 1;
      const queryOptions: PlayerOverviewsQueryOptions = {
        name: searchString,
        page: nextPageToQuery,
        pageSize: playerSimpleTablePageSize,
      };

      const result: SearchPlayerOverviewsResponse | undefined = await searchPlayerOverviews(
        queryOptions,
        currentUser,
        scoutingConfig.nameSearchHistory,
        userConfig?.email,
        userConfig?.club,
      );
      setActiveSearchString(searchString);

      if (!result) {
        // handle error?
        setEmptyTableData();
      }
      else if (result.total_hits === 0 || (isNewSearch && result.players.length === 0)) {
        setIsSearchWithoutResult(true);
        setEmptyTableData();
      }
      else {
        setIsSearchWithoutResult(false);
        setTableData(isNewSearch ? result.players : [...tableData, ...result.players]);
        setCurrentPage(result.current_page);
        setTotalHits(result.total_hits);
      }
    }

    setIsLoading(false);
  };


  const handleMessageLinkClick = () => {
    setSearchString('');
    if (isAddPlayerWithoutData) {
      setIsAddPlayerWithoutData(false);
    }
    else {
      setIsAddPlayerWithoutData(true);
    }
  };


  useEffect(() => {
    if (players) {
      const existingPlayersWithoutData: StringToAnyMap[] = [];

      Object.keys(players).forEach((playerId: PlayerId) => {
        if (isNaN(Number(playerId))) {
          existingPlayersWithoutData.push({ ...players[playerId] });
        }
      });

      setExistingPlayersWithoutData(existingPlayersWithoutData.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')));
    }
  }, [players]);


  useEffect(() => {
    if (existingPlayersWithoutDataSearchString === '') {
      setExistingPlayersWithoutDataToShow(existingPlayersWithoutData);
    }
    else {
      const fuse = new Fuse(existingPlayersWithoutData, { keys: ['name'], threshold: 0.2 });
      setExistingPlayersWithoutDataToShow(fuse.search(existingPlayersWithoutDataSearchString).map(result => result.item));
    }
  }, [existingPlayersWithoutData, existingPlayersWithoutDataSearchString]);


  const resetSearch = () => {
    setDebouncedSearchString('');
    setShouldShowSearchResult(false);
    setEmptyTableData();
    setIsSearchWithoutResult(false);
  };


  useEffect(() => {
    if (searchString.length === 0) {
      resetSearch();
    }

    else if (isAddPlayerWithoutData) {
      setShouldShowSearchResult(true);
      setIsSearchWithoutResult(false);
      setTableData(
        [{
          id: 'newPlayerWithoutData',
          name: searchString,
        }]
      );
    }

    if (!isAddPlayerWithoutData) {
      const [debounceFilter, clearDebounce] = debounce(() => {
        if (searchString !== debouncedSearchString) {
          setDebouncedSearchString(searchString);
        }
      }, 600);

      debounceFilter();

      clearDebounceRef.current = clearDebounce;

      return () => {
        clearDebounce();
      };
    }
  }, [searchString]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (debouncedSearchString !== '') {
      handleSearchForPlayers(false, true);
    }
  }, [debouncedSearchString]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    const timer = setTimeout(() => {
      setShowText(true);
    }, 250);

    return () => clearTimeout(timer);
  }, []);


  useEffect(() => {
    if (searchString !== '' && searchString === activeSearchString) {
      setShouldShowSearchResult(true);
    }
  }, [searchString, activeSearchString]);


  return (
    <div className='add-player-academy-view-container'>

      <div
        className='add-player-view-message-link fade-in'
        style={{ marginTop: '-1vh' }}
        onClick={handleMessageLinkClick}
      >
        {showText && translate(isAddPlayerWithoutData ? 'searchForPlayer?' : 'addPlayerWithoutData?', userConfig?.language)}
      </div>

      <div className='add-player-view-title'>
        {showText && translate(isAddPlayerWithoutData ? 'addPlayerWithoutData' : 'addPlayerFromTheDatabase', userConfig?.language)}
      </div>

      {showText && (
        <div
          style={{ marginTop: '10vh', width: 240 }}
          className='add-player-view-input-field-container'
        >
          <InputField
            value={searchString}
            placeholder={showText ? translate(isAddPlayerWithoutData ? 'providePlayerName' : 'searchForName', userConfig?.language) : undefined}
            onChange={onChangeSearchField}
            onKeyDown={onKeyDownSearchField}
            resetValue={() => setSearchString('')}
            showNameSearchHistory={!isAddPlayerWithoutData}
            onNameSearchHistoryClick={(name: string) => { setSearchString(name); setDebouncedSearchString(name); }}
            maxHeight={'calc(70vh - ' + (isFiltersExpanded ? '130px' : '50px') + ')'}
            isWhiteBackgroundWhenExpanded={true}
          />
        </div>
      )}

      {shouldShowSearchResult && (
        <div className='add-player-view-result-section'>

          {isSearchWithoutResult && (
            <div className='add-player-view-no-result fade-in'>
              {translate('noResult', userConfig?.language)}
            </div>
          )}

          {!isSearchWithoutResult && (
            <div className='add-player-view-table'>
              <PlayerSimpleTable
                tableData={tableData}
                onPlayerClick={(player: StringToAnyMap) => onPlayerClick(player)}
                tableType={'addPlayerAcademyView'}
                maxHeight={'calc(75vh - ' + (isFiltersExpanded ? '150px' : '70px') + ')'}

                isSearchResultTable={true}
                isLoading={isLoading}
                handleSearchForPlayers={handleSearchForPlayers}
                currentPage={currentPage}
                totalHits={totalHits}

                onAddPlayerToAcademyClick={isAddPlayerWithoutData ? () => setSearchString('') : undefined}
              />
            </div>
          )}

        </div>
      )}


      {isAddPlayerWithoutData && existingPlayersWithoutData.length > 0 && (
        <div
          className='add-player-view-existing-players'
          style={{
            height: 'calc(60vh' + (isFiltersExpanded ? ' - 100px' : ' - 30px') + ')',
            marginTop: '27vh',
            justifyContent: existingPlayersWithoutData.length > 12 ? undefined : 'center',
            transition: 'height 100ms',
          }}
        >
          <div className='add-player-view-existing-players-title'>
            {translate('existingPlayersWithoutData', userConfig?.language)}
          </div>

          {existingPlayersWithoutData.length > 12 && (
            <div className='add-player-view-input-field-container-existing-players'>
              <InputField
                value={existingPlayersWithoutDataSearchString}
                placeholder={showText ? translate('searchForName', userConfig?.language) : undefined}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setExistingPlayersWithoutDataSearchString(event.target.value)}
                resetValue={() => setExistingPlayersWithoutDataSearchString('')}
              />
            </div>
          )}

          <div className='add-player-view-players-without-data-table'>
            <PlayerSimpleTable
              tableData={existingPlayersWithoutDataToShow}
              onPlayerClick={(player: StringToAnyMap) => onPlayerClick(player)}
              tableType={'addPlayerAcademyView'}
              maxHeight={'calc(54vh ' + (isFiltersExpanded ? ' - 80px' : ' - 10px') + (existingPlayersWithoutData.length > 12 ? ' - 50px' : ' - 10px') + ')'}
            />
          </div>
        </div>
      )}

    </div>
  );
};
